// src/pages/TermsPage.jsx
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10">
    <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-slate-800">{title}</h2>
    <div className="space-y-3 text-slate-400 text-sm leading-relaxed">{children}</div>
  </section>
);

const TermsPage = () => {
  const EFFECTIVE_DATE = "March 13, 2026";
  const COMPANY = "AI Workshop LLC";
  const EMAIL = "support@aiworkshopllc.com";
  const SITE = "aiworkshopllc.com";

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <Helmet>
        <title>Terms of Service � AI Workshop LLC</title>
        <meta name="description" content="Terms of Service for AI Workshop LLC. Read before using our AI implementation blueprint service." />
      </Helmet>
      <Header />

      <div className="max-w-3xl mx-auto px-4 py-20">
        {/* Header */}
        <div className="mb-12">
          <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold mb-3">Legal</div>
          <h1 className="text-4xl font-black text-white mb-4">Terms of Service</h1>
          <p className="text-slate-500 text-sm">Effective date: {EFFECTIVE_DATE}</p>
          <p className="text-slate-400 text-sm mt-3">
            These Terms of Service ("Terms") govern your access to and use of {SITE} and the services provided by {COMPANY} ("we," "us," or "our"). By accessing our website or purchasing any product, you agree to be bound by these Terms. If you do not agree, do not use our services.
          </p>
        </div>

        {/* TOC */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 mb-12">
          <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold mb-4">Contents</p>
          <ol className="space-y-1.5 text-sm text-slate-400">
            {[
              ["1", "About Our Services"],
              ["2", "Eligibility"],
              ["3", "Purchases and Payments"],
              ["4", "Refund Policy"],
              ["5", "Promo Codes"],
              ["6", "Subscription Services"],
              ["7", "AI-Generated Content Disclaimer"],
              ["8", "Intellectual Property"],
              ["9", "Agency and White-Label API"],
              ["10", "Prohibited Uses"],
              ["11", "Limitation of Liability"],
              ["12", "Disclaimer of Warranties"],
              ["13", "Indemnification"],
              ["14", "Termination"],
              ["15", "Governing Law"],
              ["16", "Changes to These Terms"],
              ["17", "Contact Us"],
            ].map(([num, label]) => (
              <li key={num}>
                <a href={`#section-${num}`} className="hover:text-[#3b82f6] transition-colors">
                  {num}. {label}
                </a>
              </li>
            ))}
          </ol>
        </div>

        {/* Sections */}
        <Section id="section-1" title="1. About Our Services">
          <p>
            {COMPANY} operates an AI implementation advisory platform at {SITE}. Our core service is a personalized AI Implementation Blueprint ("Blueprint") � a digital report generated from information you provide about your business (industry, size, revenue, current tools, challenges, and goals).
          </p>
          <p>
            The Blueprint includes AI tool recommendations, ROI projections, a 12-week implementation roadmap, a custom prompt library, risk guidance, and competitive intelligence. The report is delivered digitally as a web report and PDF via email.
          </p>
          <p>
            We also offer: (a) a monthly subscription service ("AI Implementation OS") that provides updated recommendations over time; (b) an Agency API program for resellers and white-label partners; and (c) optional consulting or support services offered at our discretion.
          </p>
          <p>
            Our tool database and recommendations are updated regularly using automated systems. However, the AI tools landscape changes rapidly and we cannot guarantee any specific tool will remain available, affordable, or suitable for your use case after your purchase.
          </p>
        </Section>

        <Section id="section-2" title="2. Eligibility">
          <p>
            You must be at least 18 years old and have the legal authority to enter into contracts on behalf of yourself or the business you represent to use our services. By using our services, you represent that you meet these requirements.
          </p>
          <p>
            Our services are intended for business owners, operators, and professionals seeking to implement AI tools in a legitimate commercial context. Use of our services for illegal purposes, spam, deceptive practices, or harm to others is prohibited.
          </p>
        </Section>

        <Section id="section-3" title="3. Purchases and Payments">
          <p>
            The AI Implementation Blueprint is offered for a one-time fee of $149 USD ("Report Price"), subject to change. All prices are displayed in US dollars. Applicable taxes may be added based on your location.
          </p>
          <p>
            Payments are processed by Stripe, Inc., a third-party payment processor. By purchasing, you agree to Stripe's terms of service and authorize us to charge your payment method for the amount displayed at checkout. We do not store your full payment card details � they are handled entirely by Stripe.
          </p>
          <p>
            Your Blueprint will be available immediately upon successful payment via the web report URL. A PDF copy will be delivered to the email address you provided within minutes of payment. Delivery delays due to email provider filtering, server load, or incorrect email addresses are possible but not grounds for a refund.
          </p>
          <p>
            If your payment fails or is declined, no report will be unlocked. If you believe a payment was charged in error, contact us at {EMAIL} within 7 days.
          </p>
        </Section>

        <Section id="section-4" title="4. Refund Policy">
          <p>
            <strong className="text-white">All sales are final.</strong> Because our Blueprints are personalized digital reports generated and delivered immediately upon payment, we do not offer refunds once a Blueprint has been unlocked and delivered.
          </p>
          <p>
            Exceptions may be made at our sole discretion in the following circumstances: (a) a verified technical failure prevented delivery of your report; (b) you were charged twice for the same purchase; or (c) your purchase was made fraudulently without your authorization.
          </p>
          <p>
            To request an exception, email {EMAIL} within 7 days of purchase with your report ID and a description of the issue. We will respond within 3 business days.
          </p>
          <p>
            For subscription services, you may cancel at any time. Cancellation takes effect at the end of your current billing period. We do not provide prorated refunds for unused subscription time.
          </p>
        </Section>

        <Section id="section-5" title="5. Promo Codes">
          <p>
            We may offer promotional codes that discount the price of a Blueprint or subscription. Promo codes: (a) are single-use per customer unless otherwise stated; (b) cannot be combined with other offers; (c) may have expiration dates that are strictly enforced; (d) have no cash value; and (e) may be revoked or modified at our discretion without notice.
          </p>
          <p>
            Codes obtained through unauthorized channels, shared publicly in violation of their terms, or used to circumvent our pricing fraudulently may be revoked and associated accounts may be terminated.
          </p>
        </Section>

        <Section id="section-6" title="6. Subscription Services">
          <p>
            The "AI Implementation OS" subscription ("Subscription") is offered at $29 USD per month, subject to change with 30 days notice to active subscribers. The Subscription provides: auto-updating tool recommendations, weekly industry digest emails, cloud-synced progress tracking, and access to updated benchmarks.
          </p>
          <p>
            Subscriptions renew automatically on your billing date each month. By subscribing, you authorize us to charge your payment method each billing period until you cancel. You may cancel at any time through the cancellation feature in your account or by emailing {EMAIL}. Cancellation takes effect at the end of the current billing period � you retain access until then.
          </p>
          <p>
            We reserve the right to modify subscription features with 30 days notice. If we materially reduce the features of the Subscription, you may cancel without penalty within 30 days of the notice.
          </p>
          <p>
            If a subscription payment fails, we will attempt to collect it up to three times. After failed collection, your Subscription will be suspended. Your data will be retained for 90 days after suspension before deletion.
          </p>
        </Section>

        <Section id="section-7" title="7. AI-Generated Content Disclaimer">
          <p>
            Your Blueprint is generated using a combination of our proprietary rule-based engine, structured business data from your intake form, a curated database of AI tools, and large language model (LLM) AI technology from third-party providers including xAI and Anthropic.
          </p>
          <p>
            <strong className="text-white">AI-generated content may contain inaccuracies, outdated information, or recommendations that are unsuitable for your specific situation.</strong> The Blueprint is provided for informational and educational purposes only. It is not professional financial, legal, tax, or technology consulting advice.
          </p>
          <p>
            ROI projections and financial estimates in your Blueprint are illustrative projections based on industry benchmarks and historical data. They are not guarantees of specific outcomes. Actual results will vary based on your execution, market conditions, tool availability, and factors we cannot predict or control.
          </p>
          <p>
            You are solely responsible for evaluating the suitability of any AI tool, recommendation, or implementation approach described in your Blueprint before acting on it. Always conduct your own due diligence, particularly before making significant technology investments.
          </p>
        </Section>

        <Section id="section-8" title="8. Intellectual Property">
          <p>
            <strong className="text-white">Our intellectual property.</strong> The website, platform software, underlying report engine, tool database, prompt library templates, scoring methodology, and all content we create are owned by {COMPANY} and protected by copyright, trade secret, and other applicable laws. You may not copy, reproduce, reverse-engineer, scrape, or create derivative works from our platform or methodology.
          </p>
          <p>
            <strong className="text-white">Your Blueprint.</strong> Upon full payment, we grant you a personal, non-transferable, non-exclusive license to use your Blueprint for your own business purposes. You may share your Blueprint internally with employees, contractors, or advisors who work directly on your implementation. You may not resell, republish, or distribute your Blueprint to third parties for commercial gain without written permission from {COMPANY}.
          </p>
          <p>
            <strong className="text-white">Your data.</strong> You retain ownership of the business information you provide in the intake form. By submitting this information, you grant us a limited license to process and use it to generate your report and improve our services (in anonymized, aggregated form).
          </p>
        </Section>

        <Section id="section-9" title="9. Agency and White-Label API">
          <p>
            Agency API access ("Agency Program") is offered to resellers, consultants, and digital agencies who wish to generate Blueprints for their clients. Agency Program participants are subject to separate API usage terms provided at sign-up, and these Terms apply to all Agency usage.
          </p>
          <p>
            Agency participants: (a) are responsible for all reports generated under their API key; (b) may not misrepresent the origin or nature of reports to their clients; (c) are responsible for ensuring their clients' data is collected with appropriate consent; (d) may not use their API key to generate reports for purposes that violate these Terms.
          </p>
          <p>
            We reserve the right to revoke API access for violation of these Terms, abusive usage patterns, or non-payment. White-label branding features are provided as-is and we make no warranty that they will function without error.
          </p>
        </Section>

        <Section id="section-10" title="10. Prohibited Uses">
          <p>You may not use our services to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Submit false, misleading, or fraudulent information in the intake form</li>
            <li>Scrape, crawl, or systematically extract data from our platform</li>
            <li>Reverse-engineer, decompile, or attempt to extract our scoring methodology or tool database</li>
            <li>Use automated means (bots, scripts) to create multiple accounts or generate reports at scale without authorization</li>
            <li>Resell, redistribute, or sublicense your Blueprint without written permission</li>
            <li>Use our services to generate content for competitors of {COMPANY}</li>
            <li>Engage in any activity that violates applicable laws, including privacy laws, CAN-SPAM, or consumer protection regulations</li>
            <li>Attempt to gain unauthorized access to our systems, databases, or other users' accounts</li>
          </ul>
          <p>
            Violation of these prohibitions may result in immediate termination of your account and access, without refund.
          </p>
        </Section>

        <Section id="section-11" title="11. Limitation of Liability">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, {COMPANY.toUpperCase()}, ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING FROM: (a) YOUR USE OF OR INABILITY TO USE OUR SERVICES; (b) ANY CONTENT OBTAINED FROM OUR SERVICES; (c) UNAUTHORIZED ACCESS TO OR ALTERATION OF YOUR TRANSMISSIONS OR DATA; OR (d) ANY OTHER MATTER RELATING TO OUR SERVICES.
          </p>
          <p>
            OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM THESE TERMS OR OUR SERVICES SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.
          </p>
          <p>
            Some jurisdictions do not allow the exclusion of certain warranties or limitation of liability. In such jurisdictions, our liability is limited to the fullest extent permitted by law.
          </p>
        </Section>

        <Section id="section-12" title="12. Disclaimer of Warranties">
          <p>
            OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
          </p>
          <p>
            We do not warrant that: (a) the services will function uninterrupted, secure, or error-free; (b) any information provided is accurate, complete, current, or suitable for your purposes; (c) any defects or errors will be corrected; or (d) results obtained from using our services will meet your expectations.
          </p>
        </Section>

        <Section id="section-13" title="13. Indemnification">
          <p>
            You agree to indemnify, defend, and hold harmless {COMPANY} and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from: (a) your use of our services; (b) your violation of these Terms; (c) your violation of any third-party rights; or (d) any content you submit through our services.
          </p>
        </Section>

        <Section id="section-14" title="14. Termination">
          <p>
            We may suspend or terminate your access to our services at any time, with or without notice, for conduct that we determine violates these Terms, is harmful to other users, us, or third parties, or for any other reason at our sole discretion.
          </p>
          <p>
            Upon termination: your license to use your purchased Blueprint survives termination for personal use as described in Section 8; subscription access ends immediately; API access is revoked immediately. Sections 7, 8, 10, 11, 12, 13, and 15 of these Terms survive termination.
          </p>
        </Section>

        <Section id="section-15" title="15. Governing Law">
          <p>
            These Terms are governed by the laws of the State of California, without regard to its conflict of laws provisions. You agree that any dispute arising from these Terms or our services will be resolved in the state or federal courts located in Orange County, California, and you consent to the personal jurisdiction of those courts.
          </p>
          <p>
            Any claim must be brought within one (1) year of the event giving rise to the claim, or it is permanently waived.
          </p>
        </Section>

        <Section id="section-16" title="16. Changes to These Terms">
          <p>
            We may update these Terms at any time. When we do, we will update the effective date at the top of this page. For material changes, we will notify active subscribers by email at least 14 days before the change takes effect. Your continued use of our services after the effective date constitutes acceptance of the updated Terms.
          </p>
          <p>
            We encourage you to review these Terms periodically. If you do not agree to updated Terms, you must stop using our services.
          </p>
        </Section>

        <Section id="section-17" title="17. Contact Us">
          <p>
            If you have questions about these Terms, contact us at:
          </p>
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mt-3 not-prose">
            <p className="text-white font-semibold">{COMPANY}</p>
            <p className="text-slate-400 mt-1">Email: <a href={`mailto:${EMAIL}`} className="text-[#3b82f6] hover:underline">{EMAIL}</a></p>
            <p className="text-slate-400">Website: <a href={`https://${SITE}`} className="text-[#3b82f6] hover:underline">{SITE}</a></p>
          </div>
        </Section>

        {/* Footer note */}
        <div className="mt-12 pt-8 border-t border-white/8 text-center">
          <p className="text-slate-600 text-xs">
            These Terms of Service were last updated on {EFFECTIVE_DATE}. See also our{" "}
            <Link to="/privacy" className="text-[#3b82f6] hover:underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsPage;

